/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { useMouseMove } from '../../../services';
export var EuiRangeDraggable = ({
  className,
  showTicks,
  lowerPosition,
  upperPosition,
  compressed,
  onChange,
  min,
  max,
  disabled,
  value,
  ...rest
}) => {
  const outerStyle = {
    left: `calc(${lowerPosition})`,
    right: `calc(100% - ${upperPosition} - 16px)`
  };
  const classes = classNames('euiRangeDraggable', {
    'euiRangeDraggable--hasTicks': showTicks,
    'euiRangeDraggable--compressed': compressed,
    'euiRangeDraggable--disabled': disabled
  }, className);

  const handleChange = ({
    x
  }, isFirstInteraction) => {
    if (disabled) return;
    onChange(x, isFirstInteraction);
  };

  const [handleMouseDown, handleInteraction] = useMouseMove(handleChange);
  const commonProps = {
    className: classes,
    role: 'slider',
    'aria-valuemin': min,
    'aria-valuemax': max,
    'aria-valuenow': value[0],
    'aria-valuetext': `${value[0]}, ${value[1]}`,
    'aria-disabled': !!disabled,
    tabIndex: !!disabled ? -1 : 0
  };
  return <div style={outerStyle} {...commonProps} {...rest}>
      <div className="euiRangeDraggle__inner" onMouseDown={handleMouseDown} onTouchStart={handleInteraction} onTouchMove={handleInteraction} />
    </div>;
};
EuiRangeDraggable.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  value: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  disabled: PropTypes.bool,
  compressed: PropTypes.bool,
  showTicks: PropTypes.bool,
  lowerPosition: PropTypes.string.isRequired,
  upperPosition: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

try {
  EuiRangeDraggable.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/range/range_draggable.tsx',
    description: '',
    displayName: 'EuiRangeDraggable',
    methods: [],
    props: {
      min: {
        defaultValue: null,
        description: '',
        name: 'min',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      max: {
        defaultValue: null,
        description: '',
        name: 'max',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: true,
        type: {
          name: 'number[]'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTicks: {
        defaultValue: null,
        description: '',
        name: 'showTicks',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      lowerPosition: {
        defaultValue: null,
        description: '',
        name: 'lowerPosition',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      upperPosition: {
        defaultValue: null,
        description: '',
        name: 'upperPosition',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_draggable.tsx',
          name: 'EuiRangeDraggableProps'
        }],
        required: true,
        type: {
          name: '(x: number, isFirstInteraction?: boolean) => void'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiRangeDraggableProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}